import type { BufferedReaction } from '../../shared/bufferedReaction';
import type { BufferedSample } from '../../shared/bufferedSample';
import { postReviewerEvents } from '../api/postReviewerEvents';
import { pendingBatchStorage } from './pendingBatchStorage';

// Spec §4: flush every 10s and on finish. Not per tap — a request per tap wastes
// battery, and a failed one loses a reaction with nothing to notice it by.
const FLUSH_INTERVAL_MS = 10_000;

// Reactions mirror to storage the instant they happen; samples are throttled.
// A sample lost to a crash costs a notch on the retention curve, whereas a lost
// reaction is the actual data, and at 500ms a long video would otherwise
// re-serialise a growing array 7200 times.
const SAMPLE_MIRROR_INTERVAL_MS = 2000;

// Buffers taps and engagement samples, flushes them in batches, and mirrors
// everything unsent to localStorage.
//
// The unsent buffer is cleared only after the server acknowledges, and every
// event carries a per-reviewer client_seq that the server deduplicates on. That
// combination is what makes a retry safe: the client can re-send freely without
// ever having to reason about whether a timed-out request actually landed.
export function createReactionBufferStore(input: {
  reviewerId: string;
  testId: string;
  // Where to continue numbering from. A resumed reviewer MUST NOT restart at 1:
  // the server deduplicates on (reviewer_id, client_seq), so a repeated sequence
  // would be silently discarded as a retry rather than recorded as a new tap.
  startSeq?: number;
  // Called whenever the sequence advances, so the caller can persist the
  // high-water mark across page loads.
  onSeqAdvanced?: (nextSeq: number) => void;
}) {
  let nextSeq = input.startSeq && input.startSeq > 0 ? input.startSeq : 1;
  let pendingReactions: BufferedReaction[] = [];
  let pendingSamples: BufferedSample[] = [];
  let flushing = false;
  let timer: number | null = null;
  let lastSampleMirrorAt = 0;

  // Kept separately from the pending buffer because the summary screen shows the
  // reviewer their own trail (spec §5) and must still have it after a successful
  // flush has emptied everything waiting to be sent.
  const recordedReactions: BufferedReaction[] = [];

  function mirror(): void {
    pendingBatchStorage.write(input.reviewerId, {
      test_id: input.testId,
      reactions: pendingReactions,
      samples: pendingSamples
    });
  }

  async function flush(): Promise<void> {
    if (flushing) return;
    if (pendingReactions.length === 0 && pendingSamples.length === 0) return;

    flushing = true;
    // Copies, not aliases. postReviewerEvents serialises its payload up front, so
    // anything tapped while the request is in flight is NOT sent — and if these
    // were references to the live arrays, the success path below would drop those
    // taps as though they had been. That is precisely the reaction a reviewer is
    // most likely to make: one just before the connection wobbled.
    const sendingReactions = pendingReactions.slice();
    const sendingSamples = pendingSamples.slice();

    try {
      await postReviewerEvents(input.reviewerId, {
        reactions: sendingReactions,
        samples: sendingSamples
      });
      const sentReactions = new Set(sendingReactions);
      const sentSamples = new Set(sendingSamples);
      pendingReactions = pendingReactions.filter((item) => !sentReactions.has(item));
      pendingSamples = pendingSamples.filter((item) => !sentSamples.has(item));
      mirror();
      if (pendingReactions.length === 0 && pendingSamples.length === 0) {
        pendingBatchStorage.clear(input.reviewerId);
      }
    } catch {
      // Keep everything and try again on the next tick. This is the offline path
      // and it is expected to happen, not exceptional.
    } finally {
      flushing = false;
    }
  }

  function onVisibilityChange(): void {
    // A phone locking or the reviewer switching apps is the most likely moment to
    // lose a tab, and it is the last moment we are reliably given to act.
    if (document.visibilityState === 'hidden') void flush();
  }

  return {
    pushReaction(reaction: Omit<BufferedReaction, 'client_seq'>): void {
      const buffered: BufferedReaction = { ...reaction, client_seq: nextSeq };
      nextSeq += 1;
      pendingReactions.push(buffered);
      recordedReactions.push(buffered);
      input.onSeqAdvanced?.(nextSeq);
      mirror();
    },

    pushSample(sample: Omit<BufferedSample, 'client_seq'>): void {
      pendingSamples.push({ ...sample, client_seq: nextSeq });
      nextSeq += 1;
      input.onSeqAdvanced?.(nextSeq);
      const now = performance.now();
      if (now - lastSampleMirrorAt >= SAMPLE_MIRROR_INTERVAL_MS) {
        lastSampleMirrorAt = now;
        mirror();
      }
    },

    start(): void {
      if (timer !== null) return;
      timer = window.setInterval(() => void flush(), FLUSH_INTERVAL_MS);
      document.addEventListener('visibilitychange', onVisibilityChange);
    },

    async stop(): Promise<void> {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // Mirror unconditionally before the final flush: if it fails, the throttled
      // tail of samples must still be on disk for drainOrphanedBatches to find.
      mirror();
      await flush();
    },

    flushNow(): Promise<void> {
      return flush();
    },

    recorded(): BufferedReaction[] {
      return recordedReactions;
    },

    pendingCount(): number {
      return pendingReactions.length + pendingSamples.length;
    }
  };
}
